import { useState, useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { ROUTES } from '@configs/routes';
import { INPUT_TYPES } from '@constants/inputs.constants';
import { TIMEOUTS } from '@constants/auth.constants';
import authSvc from '../../../services/auth/authSvc';
import {
  legacyPasswordFormSchema,
  LegacyPasswordFormData,
} from '../../../schemas/auth.schemas';
import useAuthErrorHandler from '../../shared/hooks/useAuthErrorHandler';
import { UseLegacyPasswordResetReturn } from '../types';

const useLegacyPasswordReset = (): UseLegacyPasswordResetReturn => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getErrorMessage } = useAuthErrorHandler('reset-password');

  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const paramUuid = searchParams.get(INPUT_TYPES.UUID) || '';

  const form = useForm<LegacyPasswordFormData>({
    resolver: zodResolver(legacyPasswordFormSchema),
    defaultValues: {
      [INPUT_TYPES.PASSWORD]: '',
      [INPUT_TYPES.CONFIRM_PASSWORD]: '',
      [INPUT_TYPES.UUID]: paramUuid,
    },
    mode: 'onChange',
  });

  const { register, handleSubmit, formState, control, setValue } = form;

  const isFormValid = useMemo(() => formState.isValid, [formState.isValid]);

  const getFieldError = useCallback(
    (fieldName: keyof LegacyPasswordFormData): string => {
      const fieldError = formState.errors[fieldName];
      if (!fieldError?.message) return '';
      return t(fieldError.message);
    },
    [formState.errors, t]
  );

  const handleBackToLogin = useCallback(() => {
    navigate(ROUTES.AUTH.LOGIN);
  }, [navigate]);

  const onSubmit = useCallback(
    async (data: LegacyPasswordFormData) => {
      try {
        setIsLoading(true);
        setError('');
        setSuccess(false);

        const uuidToUse = paramUuid || data.uuid;

        if (!uuidToUse) {
          throw new Error(t('errorDefault') as string);
        }

        const response = await authSvc.legacyResetPassword({
          password: data.password,
          uuid: uuidToUse,
        });

        if (response.status === 200) {
          setSuccess(true);
          setTimeout(
            () => navigate(ROUTES.AUTH.LOGIN),
            TIMEOUTS.REDIRECT_TIMEOUT
          );
        }
      } catch (err: any) {
        const errorMessage = getErrorMessage(err);
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [navigate, getErrorMessage, paramUuid, t]
  );

  return {
    register,
    handleSubmit,
    control,
    setValue,
    formState: {
      ...formState,
      isValid: isFormValid,
    },
    success,
    error,
    isLoading,
    handleBackToLogin,
    onSubmit,
    getFieldError,
  };
};

export default useLegacyPasswordReset;
